import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SquareLogo, CustomSquareLogo } from './Logo';
import { getApiUrl } from '../config/api';
import environment from '../config/environment';
import TwoFAVerification from './TwoFAVerification';
import TwoFASetup from './TwoFASetup';
import SecurityCaptcha from './SecurityCaptcha';

const LAST_APP_ID_KEY = 'lastAppId';

const COMPANY_NAME = environment.companyName || 'Moreseg Cia. Ltda.';
const LOGIN_BUTTON_COLOR = environment.loginButtonColor || '#DC2626';

const Login = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    appid: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [isNewPasswordValid, setIsNewPasswordValid] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [show2FAVerification, setShow2FAVerification] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [twoFASecret, setTwoFASecret] = useState('');
  const [twoFAQrCode, setTwoFAQrCode] = useState('');
  const navigate = useNavigate();
  const { user, login, logout, setUserFrom2FA } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const appidParam = params.get('appid');

    if (appidParam) {
      setCredentials((prev) => ({
        ...prev,
        appid: appidParam
      }));
      try {
        localStorage.setItem(LAST_APP_ID_KEY, appidParam);
      } catch (error) {
        console.warn('No se pudo persistir appid desde la URL:', error);
      }
    } else {
      try {
        const storedAppId = localStorage.getItem(LAST_APP_ID_KEY);
        if (storedAppId) {
          setCredentials((prev) => ({
            ...prev,
            appid: storedAppId
          }));
        }
      } catch (error) {
        console.warn('No se pudo recuperar appid almacenado:', error);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value
    });

    // Limpiar error cuando el usuario empiece a escribir
    if (error) {
      setError('');
    }
  };

  // Redirigir si ya está logueado (respeta ?redirect= de alertas push)
  useEffect(() => {
    if (user) {
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
        navigate(redirect, { replace: true });
        return;
      }
      if (user.rol === 'S') {
        navigate('/dashboard-supervision');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  const attemptLogin = useCallback(async (forceAppId, options = {}) => {
    // Validaciones básicas
    if (!credentials.username.trim()) {
      setError('Por favor ingresa tu usuario');
      return;
    }
    
    if (!credentials.password.trim()) {
      setError('Por favor ingresa tu contraseña');
      return;
    }

    const appidValue = credentials.appid.trim();
    const finalAppId = (forceAppId && !appidValue) ? forceAppId : appidValue;

    if (!finalAppId) {
      const storedAppId = (() => {
        try {
          return localStorage.getItem(LAST_APP_ID_KEY) || '';
        } catch (error) {
          console.warn('No se pudo leer el appid almacenado durante el login:', error);
          return '';
        }
      })();

      if (storedAppId) {
        setCredentials((prev) => ({
          ...prev,
          appid: storedAppId
        }));
        return await attemptLogin(storedAppId, options);
      }

      const currentUrl = window.location.href;
      const isLegacyFlow = options.source === 'legacy';
      const message = isLegacyFlow
        ? `No se detectó ID de equipo. URL actual: ${currentUrl}`
        : 'No se detectó el ID de equipo. Este valor debe ser inyectado por la aplicación.';
      setError(message);
      if (isLegacyFlow) {
        console.warn('Legacy login sin appid', { currentUrl });
      }
      return;
    }

    // Verificar si se requiere CAPTCHA
    if (loginAttempts >= 3 && !isCaptchaVerified && !options.captchaVerified) {
      setShowCaptcha(true);
      setError('Por seguridad, completa el CAPTCHA para continuar');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      if (finalAppId) {
        try {
          localStorage.setItem(LAST_APP_ID_KEY, finalAppId);
        } catch (storageError) {
          console.warn('No se pudo persistir appid antes del login:', storageError);
        }
      }

      const result = await login(
        credentials.username.trim(),
        credentials.password,
        finalAppId
      );
      
      
      if (result.success) {
        // El login fue exitoso, resetear estados de seguridad
        setLoginAttempts(0);
        setIsCaptchaVerified(false);
        setShowCaptcha(false);
        // El login fue exitoso, la redirección se maneja en el useEffect
        setCredentials((prev) => ({
          username: '',
          password: '',
          appid: prev.appid
        }));
      } else if (result.requiresPasswordChange) {
        // El usuario debe cambiar su contraseña
        setCurrentUserId(result.user.id);
        setShowChangePassword(true);
        // No limpiar credenciales para que el usuario vea que debe cambiar la contraseña
      } else if (result.requires2FA) {
        // Usuario requiere verificación 2FA
        setPendingUser(result.user);
        setShow2FAVerification(true);
        return;
      } else if (result.requires2FASetup) {
        // Usuario requiere configuración de 2FA
        
        setTwoFASecret(result.secret);
        setTwoFAQrCode(result.qrCode);
        // Incluir bandera allowLoginAfter2FA si está presente
        const userWithFlag = {
          ...result.user,
          allowLoginAfter2FA: result.allowLoginAfter2FA || false
        };
        setPendingUser(userWithFlag);
        setShow2FASetup(true);
        return;
      } else {
        setError(result.error || 'Error al iniciar sesión');
        // Incrementar intentos fallidos
        setLoginAttempts(prev => prev + 1);
        // Si el login falla después de verificar CAPTCHA, resetear el estado para requerir CAPTCHA nuevamente
        if (isCaptchaVerified) {
          setIsCaptchaVerified(false);
        }
        if (loginAttempts + 1 >= 3) {
          setShowCaptcha(true);
        }
      }
    } catch (error) {
      console.error('Error en login:', error);
      setError('Error de conexión. Verifica que el servidor esté funcionando.');
      // Incrementar intentos fallidos
      setLoginAttempts(prev => prev + 1);
      // Si hay error de conexión después de verificar CAPTCHA, resetear el estado
      if (isCaptchaVerified) {
        setIsCaptchaVerified(false);
      }
      if (loginAttempts + 1 >= 3) {
        setShowCaptcha(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [credentials, login, loginAttempts, isCaptchaVerified]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const params = new URLSearchParams(window.location.search);
    const appidParam = params.get('appid');
    await attemptLogin(appidParam || null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  // Funciones para manejar CAPTCHA
  const handleCaptchaVerify = () => {
    setIsCaptchaVerified(true);
    setShowCaptcha(false);
    setError('');
    const params = new URLSearchParams(window.location.search);
    attemptLogin(params.get('appid') || null, { captchaVerified: true });
  };

  const handleCaptchaError = (error) => {
    setError(error);
  };

  const handleCaptchaCancel = () => {
    setShowCaptcha(false);
    setError('Debes completar el CAPTCHA para continuar');
  };

  // Remover la detección automática - ahora se maneja en handleSubmit

  // Validar nueva contraseña en tiempo real
  useEffect(() => {
    const isValid = validateNewPassword(changePasswordData.newPassword);
    setIsNewPasswordValid(isValid);
    
    // Limpiar confirmación si la nueva contraseña cambia
    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      setChangePasswordData(prev => ({
        ...prev,
        confirmPassword: ''
      }));
    }
  }, [changePasswordData.newPassword]);

  const handleChangePasswordChange = (e) => {
    setChangePasswordData({
      ...changePasswordData,
      [e.target.name]: e.target.value
    });
    if (changePasswordError) {
      setChangePasswordError('');
    }
  };

  const validateNewPassword = (password) => {
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[.,*_-]).{8,}$/;
    return passwordRegex.test(password);
  };

  const getPasswordRequirements = (password) => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[.,*_-]/.test(password)
    };
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!changePasswordData.newPassword) {
      setChangePasswordError('La nueva contraseña es requerida');
      return;
    }

    if (!isNewPasswordValid) {
      setChangePasswordError('La nueva contraseña debe cumplir con todos los requerimientos');
      return;
    }

    if (!changePasswordData.confirmPassword) {
      setChangePasswordError('Debes confirmar tu nueva contraseña');
      return;
    }

    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      setChangePasswordError('Las contraseñas no coinciden');
      return;
    }

    setIsChangingPassword(true);
    setChangePasswordError('');

    try {
      const response = await fetch(`${getApiUrl('AUTH', 'CHANGE_PASSWORD')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: 'More12345', // Siempre usar la contraseña por defecto
          newPassword: changePasswordData.newPassword,
          confirmPassword: changePasswordData.confirmPassword,
          userId: currentUserId // Usar el ID del usuario que hizo login
        })
      });

      const result = await response.json();

      if (result.success) {
        // Cerrar sesión y mostrar mensaje de éxito (como antes)
        await logout();
        setShowChangePassword(false);
        setChangePasswordData({
          newPassword: '',
          confirmPassword: ''
        });
        setCredentials({ username: '', password: '', appid: '' });
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setCurrentUserId(null);
        
        // Mostrar mensaje de éxito por 3 segundos
        setShowSuccessMessage(true);
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 3000);
      } else {
        setChangePasswordError(result.error || 'Error al cambiar la contraseña');
      }
    } catch (error) {
      console.error('Error cambiando contraseña:', error);
      setChangePasswordError('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleCancelChangePassword = () => {
    setShowChangePassword(false);
    setChangePasswordData({
      newPassword: '',
      confirmPassword: ''
    });
    setChangePasswordError('');
    // No limpiar credenciales para que el usuario pueda intentar de nuevo
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setCurrentUserId(null);
  };

  const handle2FAVerificationSuccess = (verifiedUser) => {
    
    setShow2FAVerification(false);
    setPendingUser(null);
    
    // Actualizar directamente el estado del usuario en el AuthContext
    if (verifiedUser) {
      setUserFrom2FA(verifiedUser);
      console.log('✅ Usuario establecido exitosamente después de 2FA');
      // La redirección se manejará automáticamente por el useEffect
    }
  };

  const handle2FAVerificationCancel = () => {
    setShow2FAVerification(false);
    setPendingUser(null);
    setCredentials({ username: '', password: '', appid: '' });
  };

  const handle2FASetupComplete = async () => {
    setShow2FASetup(false);
    setTwoFASecret('');
    setTwoFAQrCode('');
    
    // Verificar si es el segundo login (usuario ya tiene 2FA configurado)
    if (pendingUser && pendingUser.allowLoginAfter2FA) {
      // Segundo login: permitir ingreso automático al sistema
      console.log('Segundo login: permitiendo ingreso automático después de configurar 2FA');
      setUserFrom2FA(pendingUser);
      setPendingUser(null);
    } else {
      // Primer login: cerrar sesión y redirigir al login
      setPendingUser(null);
      logout();
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    }
  };

  const handle2FASetupCancel = () => {
    setShow2FASetup(false);
    setTwoFASecret('');
    setTwoFAQrCode('');
    setPendingUser(null);
    // Cerrar sesión y redirigir al login
    logout();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 flex items-center justify-center p-2 sm:p-4">
      <div className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg">
        {/* Logo Container */}
        <div className="text-center mb-6 sm:mb-8">
          {/* Logo de la empresa */}
          <CustomSquareLogo 
            src={environment.logoPath} 
            alt={`Logo ${COMPANY_NAME}`}
            size="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24"
          />
          
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mt-3 mb-1 sm:mt-4 sm:mb-2">
            {COMPANY_NAME}
          </h1>
          <p className="text-gray-600 text-sm sm:text-base md:text-lg">
            Bitácora Electrónica
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
              Iniciar Sesión
            </h2>
            <p className="text-gray-600 text-sm sm:text-base">
              Ingresa tus credenciales para continuar
            </p>
          </div>
          
          {error && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-2 sm:ml-3">
                  <p className="text-xs sm:text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label htmlFor="username" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Usuario
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    className="block w-full pl-8 sm:pl-10 pr-2 sm:pr-3 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                    placeholder="Ingresa tu usuario"
                    value={credentials.username}
                    onChange={handleChange}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    autoComplete="username"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="password" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="block w-full pl-8 sm:pl-10 pr-2 sm:pr-3 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                    placeholder="Ingresa tu contraseña"
                    value={credentials.password}
                    onChange={handleChange}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                </div>
              </div>

              {/* El ID de equipo se maneja automáticamente y no es editable por el usuario */}
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center py-2 sm:py-3 px-3 sm:px-4 border text-sm sm:text-base font-semibold rounded-lg sm:rounded-xl text-white hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105"
                style={{ backgroundColor: LOGIN_BUTTON_COLOR, borderColor: LOGIN_BUTTON_COLOR }}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-xs sm:text-sm">Iniciando sesión...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    <span className="text-xs sm:text-sm">Iniciar Sesión</span>
                  </div>
                )}
              </button>
            </div>

            {/* Footer */}
            <div className="mt-6 sm:mt-8 text-center">
              <p className="text-xs text-gray-500 leading-relaxed">
                © 2025 Bitácora Electrónica {COMPANY_NAME}.<br className="sm:hidden" />
                <span className="hidden sm:inline"> </span>Todos los derechos reservados.
              </p>
            </div>
          </form>

          {/* Modal de Cambio de Contraseña */}
          {showChangePassword && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-md">
                <div className="p-6 sm:p-8">
                  <div className="text-center mb-6">
                    <div className="mx-auto w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                      Cambio de Contraseña Requerido
                    </h2>
                    <p className="text-gray-600 text-sm sm:text-base">
                      Por seguridad, debes cambiar tu contraseña antes de continuar
                    </p>
                  </div>

                  {changePasswordError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg className="h-4 w-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-red-800">{changePasswordError}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <label htmlFor="newPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                        Ingrese la Nueva Contraseña
                      </label>
                      <div className="relative">
                        <input
                          id="newPassword"
                          name="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          required
                          className={`block w-full px-3 py-2 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                            changePasswordData.newPassword && !isNewPasswordValid 
                              ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                              : isNewPasswordValid 
                              ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                              : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                          }`}
                          placeholder="Ingresa tu nueva contraseña"
                          value={changePasswordData.newPassword}
                          onChange={handleChangePasswordChange}
                          disabled={isChangingPassword}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                          ) : (
                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      
                      {/* Lista de requerimientos */}
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-700 mb-2">Requerimientos de la contraseña:</p>
                        <ul className="space-y-1">
                          {(() => {
                            const requirements = getPasswordRequirements(changePasswordData.newPassword);
                            return [
                              { key: 'minLength', text: 'Mínimo 8 caracteres', met: requirements.minLength },
                              { key: 'hasUppercase', text: '1 letra mayúscula', met: requirements.hasUppercase },
                              { key: 'hasNumber', text: '1 número', met: requirements.hasNumber },
                              { key: 'hasSpecialChar', text: '1 caracter especial ( ,  .  -  _  * )', met: requirements.hasSpecialChar }
                            ].map((req) => (
                              <li key={req.key} className="flex items-center text-xs">
                                <div className={`w-4 h-4 mr-2 rounded-full flex items-center justify-center ${
                                  req.met ? 'bg-green-100' : 'bg-gray-100'
                                }`}>
                                  {req.met ? (
                                    <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                  )}
                                </div>
                                <span className={req.met ? 'text-green-700 font-medium' : 'text-gray-500'}>
                                  {req.text}
                                </span>
                              </li>
                            ));
                          })()}
                        </ul>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                        Confirmar Contraseña
                      </label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          required
                          className={`block w-full px-3 py-2 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                            changePasswordData.confirmPassword && changePasswordData.newPassword === changePasswordData.confirmPassword
                              ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                              : changePasswordData.confirmPassword && changePasswordData.newPassword !== changePasswordData.confirmPassword
                              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                          }`}
                          placeholder={isNewPasswordValid ? "Confirma tu nueva contraseña" : "Primero completa la nueva contraseña"}
                          value={changePasswordData.confirmPassword}
                          onChange={handleChangePasswordChange}
                          disabled={isChangingPassword || !isNewPasswordValid}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          disabled={!isNewPasswordValid}
                        >
                          {showConfirmPassword ? (
                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                          ) : (
                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      {changePasswordData.confirmPassword && changePasswordData.newPassword === changePasswordData.confirmPassword && (
                        <div className="mt-1 flex items-center text-green-600">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs font-medium">Las contraseñas coinciden</span>
                        </div>
                      )}
                      {changePasswordData.confirmPassword && changePasswordData.newPassword !== changePasswordData.confirmPassword && (
                        <div className="mt-1 flex items-center text-red-600">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs font-medium">Las contraseñas no coinciden</span>
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={handleCancelChangePassword}
                        disabled={isChangingPassword}
                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={isChangingPassword}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isChangingPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Mensaje de éxito */}
          {showSuccessMessage && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  ¡Contraseña Actualizada!
                </h3>
                <p className="text-gray-600 mb-4">
                  Tu contraseña ha sido cambiada exitosamente. Serás redirigido al login en unos segundos.
                </p>
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                  <span className="ml-2 text-sm text-gray-500">Redirigiendo...</span>
                </div>
              </div>
            </div>
          )}

          {/* Modal de Verificación 2FA */}
          {show2FAVerification && pendingUser && (
            <TwoFAVerification
              user={pendingUser}
              onVerificationSuccess={handle2FAVerificationSuccess}
              onCancel={handle2FAVerificationCancel}
            />
          )}

          {/* Modal de Configuración 2FA */}
          {show2FASetup && twoFASecret && (
            <TwoFASetup
              userId={pendingUser?.id}
              secret={twoFASecret}
              qrCode={twoFAQrCode}
              onSetupComplete={handle2FASetupComplete}
              onCancel={handle2FASetupCancel}
            />
          )}

          {/* Modal de CAPTCHA de Seguridad */}
          {showCaptcha && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <SecurityCaptcha
                onVerify={handleCaptchaVerify}
                onError={handleCaptchaError}
                onCancel={handleCaptchaCancel}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login; 